import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Row,
  Col,
  Badge,
  Table,
  Alert,
  Spinner,
  Nav,
  Tab,
  Button,
  Modal,
  Form,
  InputGroup,
} from "react-bootstrap";
import { DataGrid } from "@mui/x-data-grid";
import { CSVLink } from "react-csv";
import { useAuth } from "../contexts/AuthContext";
import { fires } from "../firebase";
import { useParams, useNavigate, Link } from "react-router-dom";
import { PieChart } from "react-minimal-pie-chart";

export default function UserDetails() {
  const { currentUser } = useAuth();
  const { uid } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  // User data states
  const [userEvents, setUserEvents] = useState([]);
  const [userOrders, setUserOrders] = useState([]);
  const [userPosts, setUserPosts] = useState([]);
  const [userInteractions, setUserInteractions] = useState([]);
  const [userPageViews, setUserPageViews] = useState([]);
  const [userAnalytics, setUserAnalytics] = useState({});

  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    if (uid) {
      loadUserData();
    }
  }, [uid]);

  const loadUserData = async () => {
    setLoading(true);
    try {
      // Load user basic information
      const userDoc = await fires.collection("users").doc(uid).get();
      if (!userDoc.exists) {
        setError("User not found");
        setLoading(false);
        return;
      }

      const userData = userDoc.data();
      const userEmail = userData.email || ""
      setUser({
        id: userDoc.id,
        ...userData,
        createdAt: userData.createdAt?.toDate?.() || new Date(),
        lastLogin: userData.lastLogin?.toDate?.() || null,
      });

            // Load user events from orders
      // We'll populate this after loading orders



      // Load user orders and events
      const ordersSnapshot = await fires
        .collection("orders")
        .where("userEmail", "==", userEmail)
        .get();
      
      const ordersData = [];
      const eventsData = [];
      const eventIds = new Set(); // To track unique events
      
      for (const orderDoc of ordersSnapshot.docs) {
        const orderData = orderDoc.data();
        ordersData.push({
          id: orderDoc.id,
          ...orderData,
          createdAt: orderData.timestamp?.toDate?.() || new Date(),
        });
        
        // Add event to events data if we have eventId and haven't seen it before
        if (orderData.eventId && !eventIds.has(orderData.eventId)) {
          eventIds.add(orderData.eventId);
          eventsData.push({
            id: orderData.eventId,
            eventTitle: orderData.title || "Unknown Event",
            action: "attended",
            timestamp: orderData.timestamp?.toDate?.() || new Date(),
            orderId: orderDoc.id,
            eventId: orderData.eventId
          });
        }
      }
      
      setUserOrders(ordersData);
      setUserEvents(eventsData);

      // Load user posts
    //   const postsSnapshot = await fires
    //     .collection("posts")
    //     .where("userId", "==", uid)
    //     .limit(20)
    //     .get();
      
      const postsData = [];
    //   for (const postDoc of postsSnapshot.docs) {
    //     const postData = postDoc.data();
    //     postsData.push({
    //       id: postDoc.id,
    //       ...postData,
    //       createdAt: postData.createdAt?.toDate?.() || new Date(),
    //     });
    //   }
      setUserPosts(postsData);

      // Load user interactions
    //   const interactionsSnapshot = await fires
    //     .collection("interactions")
    //     .where("userId", "==", uid)
    //     .orderBy("createdAt", "desc")
    //     .limit(20)
    //     .get();
      
      const interactionsData = [];
    //   for (const interactionDoc of interactionsSnapshot.docs) {
    //     const interactionData = interactionDoc.data();
    //     interactionsData.push({
    //       id: interactionDoc.id,
    //       ...interactionData,
    //       createdAt: interactionData.createdAt?.toDate?.() || new Date(),
    //     });
    //   }
      setUserInteractions(interactionsData);

      // Load user page views
      const pageViewsSnapshot = await fires
        .collection("page_views")
        .where("uid", "==", uid)
        .orderBy("timestamp", "desc")
        .limit(50)
        .get();
      
      const pageViewsData = [];
      for (const pageViewDoc of pageViewsSnapshot.docs) {
        const pageViewData = pageViewDoc.data();
        pageViewsData.push({
          id: pageViewDoc.id,
          ...pageViewData,
          timestamp: pageViewData.timestamp?.toDate?.() || new Date(),
        });
      }
      setUserPageViews(pageViewsData);

      // Calculate analytics
      const analytics = {
        totalEvents: eventsData.length,
        totalOrders: ordersData.length,
        totalSpent: ordersData.reduce((sum, order) => sum + (order.amount || 0), 0),
        totalPosts: postsData.length,
        totalInteractions: interactionsData.length,
        totalPageViews: pageViewsData.length,
        completedOrders: ordersData.filter(order => order.status === "completed").length,
      };
      setUserAnalytics(analytics);

    } catch (err) {
      setError("Failed to load user data: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = () => {
    setEditForm({
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      email: user.email || "",
      phone: user.phone || "",
      userOrg: user.userOrg || "",
      role: user.role || "user",
      status: user.status || "active",
    });
    setShowEditModal(true);
  };

  const handleSaveUser = async () => {
    try {
      await fires.collection("users").doc(uid).update(editForm);
      await loadUserData(); // Reload user data
      setShowEditModal(false);
    } catch (err) {
      setError("Failed to update user: " + err.message);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: "success",
      inactive: "secondary",
      suspended: "danger",
      pending: "warning",
    };
    return <Badge bg={variants[status] || "secondary"}>{status || "active"}</Badge>;
  };

  const getRoleBadge = (role) => {
    const variants = {
      admin: "danger",
      superadmin: "dark",
      user: "primary",
      organizer: "info",
    };
    return <Badge bg={variants[role] || "secondary"}>{role || "user"}</Badge>;
  };

  const parseUserAgent = (userAgent) => {
    if (!userAgent) return "Unknown";
    
    const ua = userAgent.toLowerCase();
    let device = "Desktop";
    let browser = "Unknown";
    let os = "Unknown";
    
    // Detect device type
    if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone') || ua.includes('ipad')) {
      device = "Mobile";
    } else if (ua.includes('tablet') || ua.includes('ipad')) {
      device = "Tablet";
    }
    
    // Detect browser
    if (ua.includes('chrome')) browser = "Chrome";
    else if (ua.includes('firefox')) browser = "Firefox";
    else if (ua.includes('safari') && !ua.includes('chrome')) browser = "Safari";
    else if (ua.includes('edge')) browser = "Edge";
    else if (ua.includes('opera')) browser = "Opera";
    
    // Detect OS
    if (ua.includes('windows')) os = "Windows";
    else if (ua.includes('mac os')) os = "macOS";
    else if (ua.includes('iphone') || ua.includes('ipad')) os = "iOS";
    else if (ua.includes('android')) os = "Android";
    else if (ua.includes('linux')) os = "Linux";
    
    return `${device} - ${browser} on ${os}`;
  };

  const parsePageViewUrl = (url) => {
    if (!url) return { pageType: "Unknown", eventId: null, domain: "Unknown" };
    
    try {
      const urlObj = new URL(url);
      const path = urlObj.pathname;
      let pageType = "Other";
      let eventId = null;
      
      // Determine page type and extract event ID
      if (path.includes('/event/')) {
        pageType = "Event Page";
        const eventMatch = path.match(/\/event\/([^\/]+)/);
        eventId = eventMatch ? eventMatch[1] : null;
      } else if (path.includes('/host/')) {
        pageType = "Host Page";
      } else if (path.includes('/about/')) {
        pageType = "About Page";
      } else if (path.includes('/category/')) {
        pageType = "Category Page";
      } else if (path === '/') {
        pageType = "Home Page";
      }
      
      return {
        pageType,
        eventId,
        domain: urlObj.hostname,
        fullUrl: url
      };
    } catch (error) {
      return { pageType: "Unknown", eventId: null, domain: "Unknown", fullUrl: url };
    }
  };

  const exportUserData = () => {
    return {
      events: userEvents.map(event => ({
        Event: event.eventTitle || "N/A",
        Action: event.action || "N/A",
        Timestamp: event.timestamp?.toLocaleString() || "N/A",
        IP: event.ip || "N/A"
      })),
      orders: userOrders.map(order => ({
        "Order ID": order.uniqueId || "N/A",
        Event: order.title || "N/A",
        Amount: order.amount || 0,
        Status: order.status || "N/A",
        "View Source": typeof order.viewData === 'object' && order.viewData 
          ? `${order.viewData.city || 'Unknown'}, ${order.viewData.region || 'Unknown'}`
          : order.viewData || "Unknown",
        "Campaign ID": typeof order.viewData === 'object' && order.viewData 
          ? order.viewData.campaignId || "N/A"
          : "N/A",
        "Country": typeof order.viewData === 'object' && order.viewData 
          ? order.viewData.country || "N/A"
          : "N/A",
        "Timezone": typeof order.viewData === 'object' && order.viewData 
          ? order.viewData.timezone || "N/A"
          : "N/A",
        "User Agent": typeof order.viewData === 'object' && order.viewData 
          ? parseUserAgent(order.viewData.fromUA)
          : "N/A",
        "Order Date": order.createdAt?.toLocaleString() || "N/A"
      }))
    };
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }



  if (error) {
    return (
      <Container className="py-4">
        <Alert variant="danger">
          {error}
          <Button variant="outline-danger" className="ms-3" onClick={() => navigate(-1)}>
            Go Back
          </Button>
        </Alert>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container className="py-4">
        <Alert variant="warning">
          User not found
          <Button variant="outline-warning" className="ms-3" onClick={() => navigate(-1)}>
            Go Back
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Card className="mb-4" style={{
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        border: "none",
        boxShadow: "0 8px 32px rgba(0,0,0,0.1)"
      }}>
        <Card.Body className="text-white">
          <Row className="align-items-center">
            <Col>
              <div className="d-flex align-items-center">
                <Button
                  variant="light"
                  size="sm"
                  onClick={() => navigate(-1)}
                  className="me-3"
                >
                  <i className="fas fa-arrow-left"></i>
                </Button>
                <div>
                  <h2 className="mb-0">
                    <i className="fas fa-user me-2"></i>
                    User Details
                  </h2>
                  <p className="mb-0 opacity-75">Comprehensive user information and analytics</p>
                </div>
              </div>
            </Col>
            <Col xs="auto">
              <Button
                variant="light"
                onClick={handleEditUser}
                className="me-2"
              >
                <i className="fas fa-edit me-1"></i>
                Edit User
              </Button>
              <Button
                variant="light"
                onClick={loadUserData}
              >
                <i className="fas fa-sync-alt me-1"></i>
                Refresh
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* User Profile Card */}
      <Row className="mb-4">
        <Col md={4}>
          <Card style={{
            border: "none",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            borderRadius: "12px"
          }}>
            <Card.Body className="text-center">
              {user.profileURL ? (
                <img
                  src={user.profileURL}
                  alt={(user.firstName || "") + " " + (user.lastName || "")}
                  className="rounded-circle mb-3"
                  width="120"
                  height="120"
                  style={{ objectFit: "cover" }}
                />
              ) : (
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center text-white mx-auto mb-3"
                  style={{
                    width: "120px",
                    height: "120px",
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    fontSize: "3rem"
                  }}
                >
                  {user.firstName?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase() || "U"}
                </div>
              )}
              <h4>{(user.firstName || "") + " " + (user.lastName || "")}</h4>
              <p className="text-muted mb-2">{user.email}</p>
              <div className="mb-3">
                {getStatusBadge(user.status)} {getRoleBadge(user.role)}
              </div>
              <div className="text-start">
                <p><strong>User ID:</strong> {user.id}</p>
                <p><strong>Phone:</strong> {user.phone || "N/A"}</p>
                <p><strong>Organization:</strong> {user.userOrg || "N/A"}</p>
                <p><strong>Created:</strong> {user.createdAt?.toLocaleDateString() || "N/A"}</p>
                <p><strong>Last Login:</strong> {user.lastLogin?.toLocaleDateString() || "Never"}</p>
                <p><strong>Email Verified:</strong> 
                  <Badge bg={user.emailVerified ? "success" : "warning"} className="ms-2">
                    {user.emailVerified ? "Yes" : "No"}
                  </Badge>
                </p>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={8}>
          <Row>
            <Col md={6}>
              <Card className="mb-3" style={{
                border: "none",
                boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                borderRadius: "12px",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white"
              }}>
                <Card.Body className="text-center">
                  <h3>{userAnalytics.totalEvents}</h3>
                  <p className="mb-0">Events Attended</p>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6}>
              <Card className="mb-3" style={{
                border: "none",
                boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                borderRadius: "12px",
                background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                color: "white"
              }}>
                <Card.Body className="text-center">
                  <h3>${userAnalytics.totalSpent.toFixed(2)}</h3>
                  <p className="mb-0">Total Spent</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="mb-3" style={{
                border: "none",
                boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                borderRadius: "12px",
                background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                color: "white"
              }}>
                <Card.Body className="text-center">
                  <h3>{userAnalytics.totalOrders}</h3>
                  <p className="mb-0">Total Orders</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Analytics Chart */}
          <Card style={{
            border: "none",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            borderRadius: "12px"
          }}>
            <Card.Body>
              <h6 className="mb-3">Activity Overview</h6>
              <div style={{ height: "200px" }}>
                <PieChart
                  data={[
                    { title: 'Events', value: userAnalytics.totalEvents, color: '#667eea' },
                    { title: 'Orders', value: userAnalytics.totalOrders, color: '#f093fb' },
                    { title: 'Posts', value: userAnalytics.totalPosts, color: '#4facfe' },
                  ]}
                  label={({ dataEntry }) => `${dataEntry.title}: ${dataEntry.value}`}
                  labelStyle={{
                    fontSize: '5px',
                    fontFamily: 'sans-serif',
                  }}
                  labelPosition={50}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tabs */}
      <Card style={{
        border: "none",
        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
        borderRadius: "12px"
      }}>
        <Card.Body>
          <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
            <Row>
              <Col md={3}>
                <Nav variant="pills" className="flex-column">
                  <Nav.Item>
                    <Nav.Link eventKey="overview">Overview</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="events">Events Attended</Nav.Link>
                  </Nav.Item>

                  <Nav.Item>
                    <Nav.Link eventKey="orders">Orders</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="posts">Posts</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="interactions">Interactions</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="pageviews">Page Views</Nav.Link>
                  </Nav.Item>
                </Nav>
              </Col>
              <Col md={9}>
                <Tab.Content>
                  <Tab.Pane eventKey="overview">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6>Recent Activity</h6>
                      <CSVLink
                        data={exportUserData().events}
                        filename={`user-${user.id}-overview.csv`}
                        className="btn btn-sm btn-outline-primary"
                      >
                        Export Data
                      </CSVLink>
                    </div>
                    <Table responsive>
                      <thead>
                        <tr>
                          <th>Activity</th>
                          <th>Details</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...userEvents.slice(0, 5), ...userOrders.slice(0, 5)]
                          .sort((a, b) => new Date(b.createdAt || b.timestamp) - new Date(a.createdAt || a.timestamp))
                          .slice(0, 10)
                          .map((item, index) => (
                            <tr key={index}>
                              <td>
                                <Badge bg="info">
                                  {item.action || item.status || "Activity"}
                                </Badge>
                              </td>
                              <td>{item.eventTitle || item.orderId || "N/A"}</td>
                              <td>{(item.createdAt || item.timestamp)?.toLocaleString() || "N/A"}</td>
                            </tr>
                          ))}
                      </tbody>
                    </Table>
                  </Tab.Pane>

                  <Tab.Pane eventKey="events">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6>Events Attended ({userEvents.length})</h6>
                      <CSVLink
                        data={exportUserData().events}
                        filename={`user-${user.id}-events.csv`}
                        className="btn btn-sm btn-outline-primary"
                      >
                        Export
                      </CSVLink>
                    </div>
                    {userEvents.length > 0 ? (
                      <Table responsive>
                        <thead>
                          <tr>
                            <th>Event</th>
                            <th>Action</th>
                            <th>Timestamp</th>
                            <th>IP Address</th>
                          </tr>
                        </thead>
                        <tbody>
                          {userEvents.map((event) => (
                            <tr key={event.id}>
                              <td>{event.eventTitle || "N/A"}</td>
                              <td>
                                <Badge bg="info">{event.action || "N/A"}</Badge>
                              </td>
                              <td>{event.timestamp?.toLocaleString() || "N/A"}</td>
                              <td>{event.ip || "N/A"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                      <Alert variant="info">No events found for this user.</Alert>
                    )}
                  </Tab.Pane>



                  <Tab.Pane eventKey="orders">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6>Orders ({userOrders.length})</h6>
                      <CSVLink
                        data={exportUserData().orders}
                        filename={`user-${user.id}-orders.csv`}
                        className="btn btn-sm btn-outline-primary"
                      >
                        Export
                      </CSVLink>
                    </div>
                    {userOrders.length > 0 ? (
                      <Table responsive>
                        <thead>
                          <tr>
                            <th>Order ID</th>
                            <th>Event</th>
                            <th>Amount</th>
                            <th>Status</th>
                            <th>View Source</th>
                            <th>Order Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {userOrders.map((order) => (
                            <tr key={order.id}>
                              <td>
                                <Link 
                                  to={`/order/${order.uniqueId}`}
                                  className="text-decoration-none"
                                >
                                  {order.uniqueId || "N/A"}
                                </Link>
                              </td>
                              <td>{order.title || "N/A"}</td>
                              <td>${order.amount || 0}</td>
                              <td>
                                <Badge bg={order.status === "completed" ? "success" : "warning"}>
                                  {order.status || "pending"}
                                </Badge>
                              </td>
                              <td>
                                <div>
                                  <Badge bg="info" className="text-wrap mb-1">
                                    {typeof order.viewData === 'object' && order.viewData 
                                      ? `${order.viewData.city || 'Unknown'}, ${order.viewData.region || 'Unknown'}`
                                      : order.viewData || "Unknown"}
                                  </Badge>
                                  {typeof order.viewData === 'object' && order.viewData && (
                                    <div className="small text-muted">
                                      {order.viewData.campaignId && `Campaign: ${order.viewData.campaignId}`}
                                      {order.viewData.country && ` | ${order.viewData.country}`}
                                      {order.viewData.timezone && ` | ${order.viewData.timezone}`}
                                      {order.viewData.fromUA && (
                                        <div className="mt-1">
                                          <small>Device: {parseUserAgent(order.viewData.fromUA)}</small>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td>{order.createdAt?.toLocaleString() || "N/A"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                      <Alert variant="info">No orders found for this user.</Alert>
                    )}
                  </Tab.Pane>

                  <Tab.Pane eventKey="posts">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6>Posts ({userPosts.length})</h6>
                    </div>
                    {userPosts.length > 0 ? (
                      <Table responsive>
                        <thead>
                          <tr>
                            <th>Title</th>
                            <th>Content</th>
                            <th>Created</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {userPosts.map((post) => (
                            <tr key={post.id}>
                              <td>{post.title || "N/A"}</td>
                              <td>{post.content?.substring(0, 100) || "N/A"}...</td>
                              <td>{post.createdAt?.toLocaleString() || "N/A"}</td>
                              <td>
                                <Badge bg={post.status === "published" ? "success" : "warning"}>
                                  {post.status || "draft"}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                      <Alert variant="info">No posts found for this user.</Alert>
                    )}
                  </Tab.Pane>

                  <Tab.Pane eventKey="interactions">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6>Interactions ({userInteractions.length})</h6>
                    </div>
                    {userInteractions.length > 0 ? (
                      <Table responsive>
                        <thead>
                          <tr>
                            <th>Type</th>
                            <th>Target</th>
                            <th>Action</th>
                            <th>Created</th>
                          </tr>
                        </thead>
                        <tbody>
                          {userInteractions.map((interaction) => (
                            <tr key={interaction.id}>
                              <td>{interaction.type || "N/A"}</td>
                              <td>{interaction.targetId || "N/A"}</td>
                              <td>
                                <Badge bg="info">{interaction.action || "N/A"}</Badge>
                              </td>
                              <td>{interaction.createdAt?.toLocaleString() || "N/A"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                      <Alert variant="info">No interactions found for this user.</Alert>
                    )}
                  </Tab.Pane>

                  <Tab.Pane eventKey="pageviews">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6>Page Views ({userPageViews.length})</h6>
                      <CSVLink
                        data={userPageViews.map(pageView => {
                          const urlInfo = parsePageViewUrl(pageView.url);
                          return {
                            "Page Type": urlInfo.pageType,
                            "Event ID": urlInfo.eventId || "N/A",
                            "Domain": urlInfo.domain,
                            "Full URL": urlInfo.fullUrl,
                            "Timestamp": pageView.timestamp?.toLocaleString() || "N/A",
                            "User Agent": parseUserAgent(pageView.fromUA)
                          };
                        })}
                        filename={`user-${user.id}-pageviews.csv`}
                        className="btn btn-sm btn-outline-primary"
                      >
                        Export
                      </CSVLink>
                    </div>
                    {userPageViews.length > 0 ? (
                      <Table responsive>
                        <thead>
                          <tr>
                            <th>Page Type</th>
                            <th>Event ID</th>
                            <th>Domain</th>
                            <th>Full URL</th>
                            <th>Timestamp</th>
                            <th>Device Info</th>
                          </tr>
                        </thead>
                        <tbody>
                          {userPageViews.map((pageView) => {
                            const urlInfo = parsePageViewUrl(pageView.url);
                            return (
                              <tr key={pageView.id}>
                                <td>
                                  <Badge bg={
                                    urlInfo.pageType === "Event Page" ? "primary" :
                                    urlInfo.pageType === "Home Page" ? "success" :
                                    urlInfo.pageType === "Host Page" ? "warning" :
                                    "secondary"
                                  }>
                                    {urlInfo.pageType}
                                  </Badge>
                                </td>
                                <td>
                                  {urlInfo.eventId ? (
                                    <Badge bg="info">{urlInfo.eventId}</Badge>
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </td>
                                <td>
                                  <Badge bg="light" text="dark">
                                    {urlInfo.domain}
                                  </Badge>
                                </td>
                                <td>
                                  <div className="small text-truncate" style={{ maxWidth: "200px" }}>
                                    {urlInfo.fullUrl}
                                  </div>
                                </td>
                                <td>{pageView.timestamp?.toLocaleString() || "N/A"}</td>
                                <td>
                                  <small>{parseUserAgent(pageView.fromUA)}</small>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    ) : (
                      <Alert variant="info">No page views found for this user.</Alert>
                    )}
                  </Tab.Pane>
                </Tab.Content>
              </Col>
            </Row>
          </Tab.Container>
        </Card.Body>
      </Card>

      {/* Edit User Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white"
        }}>
          <Modal.Title>
            <i className="fas fa-edit me-2"></i>
            Edit User: {user.firstName} {user.lastName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>First Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.firstName}
                    onChange={(e) => setEditForm({...editForm, firstName: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.lastName}
                    onChange={(e) => setEditForm({...editForm, lastName: e.target.value})}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    type="tel"
                    value={editForm.phone}
                    onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Organization</Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.userOrg}
                    onChange={(e) => setEditForm({...editForm, userOrg: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Role</Form.Label>
                  <Form.Select
                    value={editForm.role}
                    onChange={(e) => setEditForm({...editForm, role: e.target.value})}
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                    <option value="superadmin">Super Admin</option>
                    <option value="organizer">Organizer</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={editForm.status}
                onChange={(e) => setEditForm({...editForm, status: e.target.value})}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
                <option value="pending">Pending</option>
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveUser}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
} 